import React, { useEffect, useState } from 'react';
import {
  Callout,
  DefaultButton,
  DetailsList,
  DetailsListLayoutMode,
  DirectionalHint,
  getTheme,
  IColumn,
  IDetailsColumnStyles,
  SelectionMode,
  Stack,
  Text
} from '@fluentui/react';
import { AdminContext } from 'Store/Admin';
import { numericalSpacingStackTokens } from '../SettingsStyles';
import { getGroupRoles, GroupRole } from '../../../services/groupRoles.service';
import { AddGroupRoleDialog } from './AddGroupRoleDialog';
import { DeleteGroupRoleDialog } from './DeleteGroupRoleDialog';
import { EditGroupRoleDialog } from './EditGroupRoleDialog';
import { MemberGroupRoleDialog } from './MemberGroupRoleDialog';
import { SalusPrimaryButton } from '../../shared/SalusPrimaryButton';
import { trackException } from '../../../services';
import { AxiosError } from 'axios';
import { RBACSyncDialog } from './RBACSyncDialog';

export const GroupRoleComponent: React.FunctionComponent = () => {
  const { state } = React.useContext(AdminContext);
  const { palette, fonts } = getTheme();

  // our four dialogs
  const [showAddGroupRoleDialog, setShowAddGroupRoleDialog] = useState(false);
  const [showDeleteGroupRoleDialog, setShowDeleteGroupRoleDialog] = useState(false);
  const [showMemberGroupRoleDialog, setShowMemberGroupRoleDialog] = useState(false);
  const [showEditGroupRoleDialog, setShowEditGroupRoleDialog] = useState(false);
  const [showRBACSyncDialog, setShowRBACSyncDialog] = useState(false);

  // random other state
  const [activeGroupRole, setActiveGroupRole] = useState<GroupRole>();
  const [isCalloutVisible, setIsCalloutVisible] = useState<Record<string, boolean>>({});
  const [groupRoles, setGroupRoles] = useState<GroupRole[]>();

  // TODO: look into duplication of these styles in UserList and maybe more
  const columnStyles: Partial<IDetailsColumnStyles> = {
    root: {
      color: palette.black,
      fontFamily: fonts.mediumPlus.fontFamily,
      fontSize: fonts.mediumPlus.fontSize,
      lineHeight: fonts.mediumPlus.lineHeight,
      // marginRight: 20,
      selectors: {
        ':hover': { backgroundColor: palette.white }
      }
    }
  };

  // Fetch group roles
  useEffect(() => {
    if (state.queueDataState) {
      getGroupRoles(
        state.queueDataState?.tenant.tenantId,
        state.queueDataState?.title.titleId,
        state.queueDataState?.subjectType
      )
        .then(data => {
          setGroupRoles(data);
        })
        .catch((err: AxiosError) => {
          err.message = 'GroupRoleComponent: ' + err.message;
          trackException(err);
        });
    }
  }, [
    state.queueDataState,
    showAddGroupRoleDialog,
    showMemberGroupRoleDialog,
    showEditGroupRoleDialog,
    showDeleteGroupRoleDialog
  ]);

  // create list UX
  const columns: IColumn[] = [
    {
      key: 'column1',
      name: 'Group name',
      fieldName: 'groupName',
      minWidth: 200,
      maxWidth: 285,
      isResizable: false,
      styles: columnStyles
    },
    {
      key: 'column2',
      name: 'Role',
      fieldName: 'role',
      minWidth: 100,
      isResizable: false,
      styles: columnStyles
    },
    {
      key: 'column3',
      name: 'Id',
      fieldName: 'id',
      minWidth: 250,
      isResizable: false,
      styles: columnStyles
    },
    {
      key: 'column4',
      name: 'Options',
      fieldName: 'options',
      minWidth: 100,
      isResizable: false,
      styles: columnStyles
    }
  ];

  // handle group add
  const onAddGroupClick = () => {
    setShowAddGroupRoleDialog(true);
  };

  const handleDefaultBtnClick = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter') {
      const lastFocusedElement = event.target as HTMLElement;
      sessionStorage.setItem('lastFocusedElement', lastFocusedElement?.id || '');
    }
  };

  return (
    <Stack>
      <Text as={'h2'} variant={'xLarge'} style={{ height: 24 }} className={'my-2'}>
        {'Add user groups'}
      </Text>
      <Text variant={'mediumPlus'} style={{ width: 504 }}>
        {
          'Gaming Moderation users are managed through AAD security groups. Click “Add group” to add AAD security groups to your queue.'
        }
      </Text>
      <Stack style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <SalusPrimaryButton
          className="my-4"
          text="Add group"
          onClick={() => onAddGroupClick()}
          style={{ width: 130, height: 48 }}
        />
        <SalusPrimaryButton
          className="my-4"
          text="RBAC Sync Info"
          onClick={() => setShowRBACSyncDialog(true)}
          style={{ width: 160, height: 48 }}
        />
      </Stack>
      <Text as={'h3'} variant={'mediumPlus'} style={{ width: 504 }}>
        {'Manage current user groups'}
      </Text>
      <Stack horizontal tokens={numericalSpacingStackTokens} className={'mt-3'}></Stack>

      {groupRoles && (
        <DetailsList
          ariaLabelForGrid="Manage current user groups"
          items={groupRoles}
          columns={columns}
          onRenderItemColumn={(item?: GroupRole, index?: number, column?: IColumn): JSX.Element => {
            if (item === undefined || index === undefined || column === undefined) {
              return <div></div>;
            }

            const htmlSafeId = `group-role-${item.id}`;
            if (column.fieldName === 'groupName') {
              return <Stack>{item.groupName}</Stack>;
            } else if (column.fieldName === 'role') {
              return <Stack>{item.role}</Stack>;
            } else if (column.fieldName === 'id') {
              return <Stack>{item.id}</Stack>;
            } else if (column.fieldName === 'options') {
              return (
                <>
                  <DefaultButton
                    aria-label="more options"
                    id={`group-role-${item.id}`}
                    onClick={() => {
                      const calloutDict = {
                        ...isCalloutVisible
                      };
                      calloutDict[htmlSafeId] = true;
                      setIsCalloutVisible(calloutDict);
                    }}
                    text={'...'}
                    style={{ border: 'none' }}
                    aria-expanded={isCalloutVisible[htmlSafeId] || false}
                    className={'moreOption'}
                    onKeyDown={handleDefaultBtnClick}
                  />
                  {isCalloutVisible[htmlSafeId] ? (
                    <Callout
                      ariaLabelledBy={'labelId'}
                      ariaDescribedBy={'descriptionId'}
                      target={`#group-role-${item.id}`}
                      isBeakVisible={false}
                      onDismiss={() => {
                        const calloutDict = {
                          ...isCalloutVisible
                        };
                        calloutDict[htmlSafeId] = false;
                        setIsCalloutVisible(calloutDict);
                      }}
                      directionalHint={DirectionalHint.topRightEdge}
                      setInitialFocus
                    >
                      <Stack>
                        <DefaultButton
                          style={{ textAlign: 'left', borderRadius: 0 }}
                          onClick={() => {
                            setActiveGroupRole(item);
                            setShowMemberGroupRoleDialog(true);
                          }}
                        >
                          See Members
                        </DefaultButton>
                        <DefaultButton
                          style={{ textAlign: 'left', borderRadius: 0 }}
                          onClick={() => {
                            setActiveGroupRole(item);
                            setShowEditGroupRoleDialog(true);
                          }}
                        >
                          Edit Role
                        </DefaultButton>
                        <DefaultButton
                          style={{ textAlign: 'left', borderRadius: 0 }}
                          onClick={() => {
                            setActiveGroupRole(item);
                            setShowDeleteGroupRoleDialog(true);
                          }}
                        >
                          Delete
                        </DefaultButton>
                      </Stack>
                    </Callout>
                  ) : null}
                </>
              );
            } else {
              return <div></div>;
            }
          }}
          setKey="set"
          layoutMode={DetailsListLayoutMode.justified}
          selectionMode={SelectionMode.none}
        />
      )}
      {showRBACSyncDialog && state.queueDataState && (
        <RBACSyncDialog
          queueDataState={state.queueDataState}
          showRBACSyncDialog={showRBACSyncDialog}
          setShowRBACSyncDialog={setShowRBACSyncDialog}
        />
      )}
      {showAddGroupRoleDialog && (
        <AddGroupRoleDialog
          showAddGroupRoleDialog={showAddGroupRoleDialog}
          setShowAddGroupRoleDialog={setShowAddGroupRoleDialog}
        />
      )}
      {showMemberGroupRoleDialog && activeGroupRole && (
        <MemberGroupRoleDialog
          showMemberGroupRoleDialog={showMemberGroupRoleDialog}
          setShowMemberGroupRoleDialog={setShowMemberGroupRoleDialog}
          activeGroupRole={activeGroupRole}
        />
      )}
      {showEditGroupRoleDialog && activeGroupRole && (
        <EditGroupRoleDialog
          showEditGroupRoleDialog={showEditGroupRoleDialog}
          setShowEditGroupRoleDialog={setShowEditGroupRoleDialog}
          activeGroupRole={activeGroupRole}
        />
      )}
      {showDeleteGroupRoleDialog && activeGroupRole && (
        <DeleteGroupRoleDialog
          showDeleteGroupRoleDialog={showDeleteGroupRoleDialog}
          setShowDeleteGroupRoleDialog={setShowDeleteGroupRoleDialog}
          activeGroupRole={activeGroupRole}
        />
      )}
    </Stack>
  );
};
